import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

interface Props {
    videoId: string;
}

const InstagramEmbed = ({ videoId }: Props) => {
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [retryCount, setRetryCount] = useState(0);
	const maxRetries = 3;
	const retryDelay = 1000; // 1 second

	// Function to process Instagram embeds
	const processEmbeds = () => {
		if (window.instgrm && window.instgrm.Embeds) {
			window.instgrm.Embeds.process();
			setIsLoading(false);
			return true;
		}
		return false;
	};

	// Function to load Instagram script
	const loadInstagramScript = () => {
		return new Promise<void>((resolve, reject) => {
			// Remove any existing script
			const existingScript = document.querySelector('script[src="//www.instagram.com/embed.js"]');
			if (existingScript) {
				existingScript.remove();
			}

			const script = document.createElement('script');
			script.src = '//www.instagram.com/embed.js';
			script.async = true;
			script.onload = () => {
				// Give Instagram's script a moment to initialize
				setTimeout(() => {
					if (processEmbeds()) {
						resolve();
					} else {
						reject(new Error('Instagram embed processing failed'));
					}
				}, 100);
			};
			script.onerror = () => reject(new Error('Failed to load Instagram script'));
			document.body.appendChild(script);
		});
	};

	// Retry mechanism
	const retryLoad = async () => {
		if (retryCount >= maxRetries) {
			setError('Failed to load Instagram embed after multiple attempts');
			setIsLoading(false);
			return;
		}

		try {
			await loadInstagramScript();
		} catch (err) {
			setRetryCount(prev => prev + 1);
			setTimeout(retryLoad, retryDelay);
		}
	};

	useEffect(() => {
		let mounted = true;

		const initializeEmbed = async () => {
			setIsLoading(true);
			setError(null);
			setRetryCount(0);

			try {
				// First try to process existing embeds
				if (!processEmbeds()) {
					// If that fails, try loading the script
					await loadInstagramScript();
				}
			} catch (err) {
				if (mounted) {
					retryLoad();
				}
			}
		};

		initializeEmbed();

		return () => {
			mounted = false;
		};
	}, [videoId]);

	// Create the Instagram embed HTML
	const instagramEmbedHtml = `
		<blockquote 
			class="instagram-media" 
			data-instgrm-captioned 
			data-instgrm-permalink="https://www.instagram.com/reel/${videoId}/?utm_source=ig_embed&amp;utm_campaign=loading" 
			data-instgrm-version="14" 
			style="background:#FFF; border:0; border-radius:3px; box-shadow:0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15); margin: 1px; max-width:540px; min-width:326px; padding:0; width:99.375%; width:-webkit-calc(100% - 2px); width:calc(100% - 2px);">
		</blockquote>
	`;

	return (
		<div style={{ minHeight: '450px', position: 'relative' }}>
			{isLoading && (
				<Box 
					display="flex" 
					flexDirection="column" 
					alignItems="center" 
					gap={2}
					position="absolute"
					top="50%"
					left="50%"
					sx={{ transform: 'translate(-50%, -50%)' }}
				>
					<CircularProgress />
					<Typography variant="body2" color="text.secondary">
						Loading Instagram content...
					</Typography>
				</Box>
			)}
			
			{error && (
				<Typography 
					color="error" 
					align="center"
					position="absolute"
					top="50%"
					left="50%"
					sx={{ transform: 'translate(-50%, -50%)' }}
				>
					{error}
				</Typography>
			)}
			
			<div
				dangerouslySetInnerHTML={{ __html: instagramEmbedHtml }}
				style={{ 
					opacity: isLoading ? 0 : 1,
					transition: 'opacity 0.3s ease-in-out'
				}}
			/>
		</div>
	);
};

// Add TypeScript declaration for the Instagram global object
declare global {
	interface Window {
		instgrm?: {
			Embeds: {
				process: () => void;
			};
		};
	}
}

export default InstagramEmbed;
